import { UAParser } from '@ua-parser-js/pro-business';

type InAppBrowser =
  | 'inapp_ios_line'
  | 'inapp_ios_facebook'
  | 'inapp_android_yahoo'
  | 'inapp_android_instagram'
  | 'inapp_android_gunosy'
  | 'inapp_android_line'
  | 'inapp_android_facebook';

const MinimumVersion = {
  iOS: 11,
  android: 6
} as const;

/**
 * Chromeの最低バージョン
 * Andoird TabletのPCモードではAndroidバージョンが取得できないため、Chromeバージョンで判定する
 * @see https://en.wikipedia.org/wiki/Template:Google_Chrome_release_compatibility
 */
const MinimumChromeVersion = 96 as const; // android 6で利用できる最低バージョン

/**
 * サポートしないブラウザ
 *
 * SkyLeap はUAParser.jsで判定できないため、isSupportedBrowser()内で判定する
 * @see https://github.com/faisalman/ua-parser-js#methods (getBrowser method)
 *
 * iOSのPuffinブラウザは　Puffin Incognito Browser / Puffin Web Browser の2つがある
 * Puffin Web BrowserはApplicant（Vue）でブラウザ判定が動作しないのでエラー表示にできない（そもそも動かないはず）
 * ApplicantJS上では正常にブザウザ判定が動作する
 */
const unsupportedPCBrowsers = ['IE', 'Opera'];
const unsupportedAndroidBrowsers = [
  'Mobile Firefox',
  'Edge',
  'Puffin',
  'Opera',
  'Opera Mini',
  'Opera Mobi',
  'Opera Tablet',
  'Opera Touch'
];
const unsupportedIOSBrowsers = [
  'Puffin',
  'Opera',
  'Opera Mini',
  'Opera Mobi',
  'Opera Tablet',
  'Opera Coast',
  'Opera Touch'
];

const ua = new UAParser();
const uaString = ua.getUA();
const os = ua.getOS();
export const osVersion = os.version;
export const osMajorVersion: number = parseInt((os.version ?? '0').split('.')[0] ?? 0, 10);
export const osMinorVersion: number = parseInt((os.version ?? '0').split('.')[1] ?? 0, 10);
export const browser = ua.getBrowser();
const device = ua.getDevice().withFeatureCheck();
const deviceWithClientHints = (async () => await ua.getDevice().withClientHints())();

/**
 * デバイス判定
 */
const isMobile = device.is('mobile') || device.is('tablet') || false; // mobile,tablet = mobile扱い
const isAndroidTabletPCMode = os.is('Linux') && navigator.maxTouchPoints > 1 && !isMobile;
const isIPad = device.is('iPad');
export const isGalaxyJ5 = deviceWithClientHints.then((device) => {
  return (device.is('Galaxy j5') || device.model?.startsWith('SM-J5')) && os.is('Android');
});

export const isPC = !isMobile && !isIPad && !isAndroidTabletPCMode; // mobile,tablet,ipad,android tabletのPCモード 以外 = PC扱い
const isEmulator = uaString.includes('sdk_gphone') || device.is('Android SDK'); // Android emulator

/**
 * 個別ブラウザ判定
 */
export const isFirefox = browser.is('Firefox');

// iOS バージョン判定
export const isIOS12Under = os.is('iOS') && osMajorVersion < 13; // iOS12以下
export const isIOS13Over = !isIOS12Under; // iOS13以上, 将来定期にUAが変更・廃止になる可能性があるため、iOS13以上の判定はiOS12以下の否定で判定する

/**
 * Os Type, only supports Android/iOS/iPad/PC
 */
export enum OsType {
  PC = 'Pc',
  iOS = 'iOS',
  Android = 'AndroidOS',
  iPad = 'iPad',
  Unsupported = 'Unsupported'
}

/**
 * OS判定
 * @see https://github.com/faisalman/ua-parser-js#methods (getOS method)
 */
export const getOSType = (): OsType => {
  if (isPC) return OsType.PC;
  if (isIPad) return OsType.iPad;
  if (os.is('iOS')) return OsType.iOS;
  if (os.is('Android') || os.is('Android-x86') || isAndroidTabletPCMode) return OsType.Android;

  return OsType.Unsupported;
};

/**
 * 対応OSかどうか
 */
export const isSupportedOSType = (): boolean => {
  if (isEmulator) return false;

  const osType = getOSType();
  return [OsType.PC, OsType.iOS, OsType.Android, OsType.iPad].includes(osType);
};

/**
 * 対応OSバージョンかどうか
 */
export const isSupportedOSVersion = (): boolean => {
  if (isEmulator) return false;

  const osType = getOSType();
  if (osType === OsType.PC) return true;
  if (osType === OsType.iPad) return true; // iPad OSはiOS 13以上なので常にtrue

  if (osType === OsType.iOS && osMajorVersion >= MinimumVersion.iOS) return true;
  if (osType === OsType.Android && osMajorVersion >= MinimumVersion.android) return true;

  // Android TabletのPCモードはAndroidバージョンが取れないので、Chromeバージョンで判定する
  if (isAndroidTabletPCMode && browser.is('Chrome') && parseInt(browser.version || '') > MinimumChromeVersion)
    return true;

  return false;
};

/**
 * 対応ブラウザかどうか
 */
export const isSupportedBrowser = (): boolean => {
  if (isEmulator) return false;

  const osType = getOSType();

  if (osType === OsType.PC) {
    if (unsupportedPCBrowsers.includes(browser.name || '')) return false;
    return true;
  }

  if (osType === OsType.iOS || osType === OsType.iPad) {
    // EKYCDEV-1971 離脱率改善のためにiOSではブラウザ判定をやめる

    // 例外：EKYCDEV-2830 一部サポート外とする
    if (unsupportedIOSBrowsers.includes(browser.name || '')) return false;

    if (checkInAppBrowser() !== null) return false;
    return true;
  }

  if (osType === OsType.Android) {
    // SkyLeap はUAParser.jsで判定できないため、ここで判定する
    if (uaString.includes('SkyLeap')) return false;

    if (unsupportedAndroidBrowsers.includes(browser.name || '')) return false;
    if (checkInAppBrowser() !== null) return false;

    return true;
  }

  return false;
};

/**
 * アプリ内ブラウザ判定
 */
export const checkInAppBrowser = (): InAppBrowser | null => {
  const osType = getOSType();

  if (osType === OsType.iOS || osType === OsType.iPad) {
    if (browser.is('Line')) return 'inapp_ios_line';
    if (browser.is('Facebook')) return 'inapp_ios_facebook';
  }

  if (osType === OsType.Android) {
    // Yahoo!ブラウザ, GunosyはUAParser.jsで判定できないため、UA文字列から判定する
    if (uaString.includes('YJApp-ANDROID')) return 'inapp_android_yahoo';
    if (uaString.includes('Gunosy')) return 'inapp_android_gunosy';

    if (browser.is('Instagram')) return 'inapp_android_instagram';
    if (browser.is('Line')) return 'inapp_android_line';
    if (browser.is('Facebook')) return 'inapp_android_facebook';
  }

  return null;
};

/**
 * カメラが使用可能かどうか
 */
export function isCameraUsable(): boolean {
  if (!window.navigator) return false;
  if (!window.navigator.mediaDevices) return false;
  if (!window.navigator.mediaDevices.getUserMedia) return false;
  if (!window.navigator.mediaDevices.getSupportedConstraints) return false;
  const constraints: MediaTrackSupportedConstraints = window.navigator.mediaDevices.getSupportedConstraints();
  if (!constraints.width) return false;
  if (!constraints.facingMode) return false;
  return true;
}
